import React, { useState, useEffect } from 'react';
import { Webhook, Download, CheckCircle2, Bell, Settings, ChevronDown, Save, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import docflowService from '../services/docflowService';

const WEBHOOK_EVENTS = [
  { id: 'signed', label: 'Signed', description: 'When document is signed by recipient' },
  { id: 'opened', label: 'Opened', description: 'When document is opened via link' },
  { id: 'sent', label: 'Sent', description: 'When document is sent to recipient' },
  { id: 'approved', label: 'Approved', description: 'When approver approves the document' },
  { id: 'rejected', label: 'Rejected', description: 'When approver rejects the document' },
  { id: 'reviewed', label: 'Reviewed', description: 'When reviewer completes review' },
  { id: 'signed_copy', label: 'Signed Copy', description: 'When signed copy is generated' },
  { id: 'completed', label: 'Completed', description: 'When document is fully completed by all recipients' }
];

// Legacy event-id migration for templates configured before the past-tense
// rename (approve→approved, etc.). Applied when reading saved config so
// existing templates keep firing the right events.
const LEGACY_EVENT_MIGRATIONS = {
  approve: ['approved'],
  reject: ['rejected'],
  review: ['reviewed'],
  approve_reject: ['approved', 'rejected'],
};
const migrateSavedEvents = (saved) => {
  if (!Array.isArray(saved)) return saved;
  const out = [];
  for (const e of saved) {
    if (LEGACY_EVENT_MIGRATIONS[e]) {
      for (const m of LEGACY_EVENT_MIGRATIONS[e]) {
        if (!out.includes(m)) out.push(m);
      }
    } else if (!out.includes(e)) {
      out.push(e);
    }
  }
  return out;
};

const SAMPLE_PAYLOADS = {
  signed: {
    event: 'signed',
    timestamp: '2026-03-30T14:30:00Z',
    template_id: 'tpl_abc123',
    template_name: 'NDA Agreement',
    tenant_id: 'tenant_abc123',
    data: {
      document_id: 'doc_abc123',
      document_status: 'signed',
      template_name: 'NDA Agreement',
      recipient_email: 'signer@example.com',
      recipient_name: 'John Doe',
      crm_object_type: 'Contact',
      crm_object_id: 'sf_contact_123',
      field_data: {
        'text_field_001': 'John Doe',
        'date_field_002': '2026-03-30',
        'account.name': 'Acme Corp',
        'contact.title': 'CEO'
      },
      merge_fields: {
        'account.name': 'Acme Corp',
        'contact.title': 'CEO'
      },
      signed_documents: [{
        document_id: 'doc_abc123',
        template_name: 'NDA Agreement',
        signed_document_url: 'https://storage.example.com/signed/doc_abc123.pdf',
        signed_at: '2026-03-30T14:30:00Z'
      }]
    },
    metadata: { ip_address: '203.0.113.42', user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36', browser: { name: 'Chrome', version: '120.0.0.0' }, os: { name: 'Windows', version: '10' }, device: { type: 'desktop' }, performed_by: 'John Doe', performed_by_email: 'signer@example.com' }
  },
  opened: {
    event: 'opened',
    timestamp: '2026-03-30T13:45:00Z',
    template_id: 'tpl_abc123',
    template_name: 'NDA Agreement',
    tenant_id: 'tenant_abc123',
    data: {
      document_id: 'doc_abc123',
      document_status: 'sent',
      recipient_email: 'user@example.com',
      recipient_name: 'John Doe',
      crm_object_type: 'Contact',
      crm_object_id: 'sf_contact_123'
    },
    metadata: { ip_address: '203.0.113.42', user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Safari/605.1.15', browser: { name: 'Safari', version: '17.0' }, os: { name: 'macOS', version: '10.15.7' }, device: { type: 'desktop' } }
  },
  sent: {
    event: 'sent',
    timestamp: '2026-03-30T12:00:00Z',
    template_id: 'tpl_abc123',
    template_name: 'NDA Agreement',
    tenant_id: 'tenant_abc123',
    data: {
      document_id: 'doc_abc123',
      document_status: 'sent',
      recipient_email: 'user@example.com',
      recipient_name: 'John Doe',
      crm_object_type: 'Contact',
      crm_object_id: 'sf_contact_123'
    }
  },
  approved: {
    event: 'approved',
    timestamp: '2026-03-30T15:00:00Z',
    template_id: 'tpl_abc123',
    template_name: 'NDA Agreement',
    tenant_id: 'tenant_abc123',
    data: {
      document_id: 'doc_abc123',
      document_status: 'approved',
      action: 'approved',
      recipient_email: 'approver@example.com',
      recipient_name: 'Jane Smith',
      role_type: 'APPROVE_REJECT',
      crm_object_type: 'Contact',
      crm_object_id: 'sf_contact_123'
    },
    metadata: { ip_address: '198.51.100.23', user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36', browser: { name: 'Chrome', version: '120.0.0.0' }, os: { name: 'macOS', version: '10.15.7' }, device: { type: 'desktop' }, performed_by: 'Jane Smith', performed_by_email: 'approver@example.com' }
  },
  rejected: {
    event: 'rejected',
    timestamp: '2026-03-30T15:02:00Z',
    template_id: 'tpl_abc123',
    template_name: 'NDA Agreement',
    tenant_id: 'tenant_abc123',
    data: {
      document_id: 'doc_abc123',
      document_status: 'declined',
      action: 'rejected',
      recipient_email: 'approver@example.com',
      recipient_name: 'Jane Smith',
      role_type: 'APPROVE_REJECT',
      reason: 'Terms not acceptable',
      crm_object_type: 'Contact',
      crm_object_id: 'sf_contact_123'
    },
    metadata: { ip_address: '198.51.100.23', user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36', browser: { name: 'Chrome', version: '120.0.0.0' }, os: { name: 'macOS', version: '10.15.7' }, device: { type: 'desktop' }, performed_by: 'Jane Smith', performed_by_email: 'approver@example.com' }
  },
  reviewed: {
    event: 'reviewed',
    timestamp: '2026-03-30T14:50:00Z',
    template_id: 'tpl_abc123',
    template_name: 'NDA Agreement',
    tenant_id: 'tenant_abc123',
    data: {
      document_id: 'doc_abc123',
      document_status: 'reviewed',
      action: 'reviewed',
      recipient_email: 'reviewer@example.com',
      recipient_name: 'Bob Wilson',
      role_type: 'VIEW_ONLY',
      crm_object_type: 'Contact',
      crm_object_id: 'sf_contact_123'
    },
    metadata: { ip_address: '192.0.2.55', user_agent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Mobile/15E148 Safari/604.1', browser: { name: 'Safari', version: '17.0' }, os: { name: 'iOS', version: '17.0' }, device: { type: 'mobile' }, performed_by: 'Bob Wilson', performed_by_email: 'reviewer@example.com' }
  },
  signed_copy: {
    event: 'signed_copy',
    timestamp: '2026-03-30T15:05:00Z',
    template_id: 'tpl_abc123',
    template_name: 'NDA Agreement',
    tenant_id: 'tenant_abc123',
    data: {
      document_id: 'doc_abc123',
      template_name: 'NDA Agreement',
      crm_object_type: 'Contact',
      crm_object_id: 'sf_contact_123',
      field_data: {
        'text_field_001': 'John Doe',
        'date_field_002': '2026-03-30',
        'account.name': 'Acme Corp',
        'contact.title': 'CEO'
      },
      merge_fields: {
        'account.name': 'Acme Corp',
        'contact.title': 'CEO'
      },
      signed_documents: [{
        document_id: 'doc_abc123',
        template_name: 'NDA Agreement',
        signed_document_url: 'https://storage.example.com/signed/doc_abc123.pdf',
        signed_at: '2026-03-30T15:05:00Z'
      }],
      generated_at: '2026-03-30T15:05:00Z'
    }
  },
  completed: {
    event: 'completed',
    timestamp: '2026-03-30T15:10:00Z',
    template_id: 'tpl_abc123',
    template_name: 'NDA Agreement',
    tenant_id: 'tenant_abc123',
    data: {
      document_id: 'doc_abc123',
      document_status: 'signed',
      template_name: 'NDA Agreement',
      status: 'completed',
      completed_at: '2026-03-30T15:10:00Z',
      final_document_url: 'https://storage.example.com/signed/doc_abc123.pdf',
      field_data: {
        'text_field_001': 'John Doe',
        'date_field_002': '2026-03-30',
        'account.name': 'Acme Corp',
        'contact.title': 'CEO'
      },
      merge_fields: {
        'account.name': 'Acme Corp',
        'contact.title': 'CEO'
      },
      signed_documents: [{
        document_id: 'doc_abc123',
        template_name: 'NDA Agreement',
        signed_document_url: 'https://storage.example.com/signed/doc_abc123.pdf',
        signed_at: '2026-03-30T15:10:00Z'
      }]
    }
  }
};

const IntegrationTab = ({ templateData, onUpdate }) => {
  const [webhookUrl, setWebhookUrl] = useState(templateData?.webhook_config?.url || '');
  const [webhookEvents, setWebhookEvents] = useState(() => {
    const saved = migrateSavedEvents(templateData?.webhook_config?.events || ['signed', 'opened']);
    return saved.includes('completed') ? saved : [...saved, 'completed'];
  });
  const [webhookHeaders, setWebhookHeaders] = useState(templateData?.webhook_config?.headers || {});
  const [webhookSecret, setWebhookSecret] = useState(templateData?.webhook_config?.secret || '');
  const [retryEnabled, setRetryEnabled] = useState(templateData?.webhook_config?.retry_enabled !== false);
  const [maxRetries, setMaxRetries] = useState(templateData?.webhook_config?.max_retries || 3);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const handleSaveWebhook = async () => {
    const templateId = templateData?.id;
    if (!templateId) {
      toast.error('Template must be saved before webhook settings can be stored');
      return;
    }
    setSaving(true);
    try {
      await docflowService.updateTemplateWebhook(templateId, {
        url: webhookUrl,
        events: webhookEvents,
        headers: webhookHeaders,
        secret: webhookSecret,
        retry_enabled: retryEnabled,
        max_retries: maxRetries,
      });
      setLastSaved(new Date());
      toast.success('Webhook settings saved');
    } catch (err) {
      toast.error('Failed to save webhook settings');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (onUpdate) {
      onUpdate({
        webhook_config: {
          url: webhookUrl,
          events: webhookEvents,
          headers: webhookHeaders,
          secret: webhookSecret,
          retry_enabled: retryEnabled,
          max_retries: maxRetries
        }
      });
    }
  }, [webhookUrl, webhookEvents, webhookHeaders, webhookSecret, retryEnabled, maxRetries]);

  const toggleEvent = (eventId) => {
    setWebhookEvents(prev =>
      prev.includes(eventId)
        ? prev.filter(e => e !== eventId)
        : [...prev, eventId]
    );
  };

  const generateWebhookUrl = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const templateId = templateData?.id || 'template-id';
    setWebhookUrl(`${baseUrl}/api/docflow/webhook/${templateId}/events`);
  };

  const handleDownloadConfig = () => {
    const config = {
      webhook_url: webhookUrl,
      events: webhookEvents.map(eventId => {
        const event = WEBHOOK_EVENTS.find(e => e.id === eventId);
        return {
          id: eventId,
          label: event?.label || eventId,
          description: event?.description || '',
          sample_payload: SAMPLE_PAYLOADS[eventId] || { event: eventId }
        };
      }),
      settings: {
        secret: webhookSecret ? '***' : null,
        retry_enabled: retryEnabled,
        max_retries: maxRetries,
        headers: webhookHeaders
      },
      template_id: templateData?.id,
      template_name: templateData?.name,
      generated_at: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `webhook-config-${templateData?.name || 'template'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Webhook config downloaded');
  };

  return (
    <div className="space-y-5" data-testid="integration-tab">
      {/* Webhook URL */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Webhook className="h-5 w-5 text-indigo-600" />
          Webhook URL
        </h3>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              data-testid="webhook-url-input"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://api.example.com/docflow/webhook/..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm pr-10"
            />
            {webhookUrl && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-green-500" title="Active" />
            )}
          </div>
          <button
            data-testid="download-webhook-config-btn"
            onClick={handleDownloadConfig}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            title="Download webhook config with sample payloads"
          >
            <Download className="h-4 w-4" />
            Download
          </button>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          {lastSaved ? (
            <span className="text-[11px] text-gray-400 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-green-500" />
              Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          ) : (
            <span className="text-[11px] text-gray-400">Changes are not saved until you click Save</span>
          )}
          <button
            data-testid="save-webhook-btn"
            onClick={handleSaveWebhook}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving...' : 'Save Webhook Settings'}
          </button>
        </div>
      </div>

      {/* Event Notifications */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Bell className="h-5 w-5 text-indigo-600" />
          Send Event Notifications
        </h3>
        <div className="space-y-2">
          {WEBHOOK_EVENTS.map(event => (
            <label
              key={event.id}
              data-testid={`webhook-event-${event.id}`}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                webhookEvents.includes(event.id)
                  ? 'border-indigo-300 bg-indigo-50/50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={webhookEvents.includes(event.id)}
                onChange={() => toggleEvent(event.id)}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <div className="flex-1">
                <span className="font-medium text-sm text-gray-900">{event.label}</span>
                <p className="text-xs text-gray-500 mt-0.5">{event.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Advanced Settings */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
        >
          <span className="flex items-center gap-2 font-semibold text-sm text-gray-900">
            <Settings className="h-4 w-4 text-gray-500" />
            Advanced Settings
          </span>
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
        </button>

        {showAdvanced && (
          <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Webhook Secret</label>
              <input
                type="text"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="Optional security secret"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
              />
              <p className="text-[10px] text-gray-500 mt-1">Used to verify webhook payloads (HMAC signature)</p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="retry-enabled"
                checked={retryEnabled}
                onChange={(e) => setRetryEnabled(e.target.checked)}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <label htmlFor="retry-enabled" className="text-sm font-medium text-gray-700">
                Enable retry on failure
              </label>
            </div>

            {retryEnabled && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Max Retries</label>
                <select
                  value={maxRetries}
                  onChange={(e) => setMaxRetries(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                >
                  <option value="1">1 retry</option>
                  <option value="3">3 retries</option>
                  <option value="5">5 retries</option>
                  <option value="10">10 retries</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Custom Headers (JSON)</label>
              <textarea
                value={JSON.stringify(webhookHeaders, null, 2)}
                onChange={(e) => {
                  try {
                    setWebhookHeaders(JSON.parse(e.target.value));
                  } catch (err) { /* ignore parse errors while typing */ }
                }}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm font-mono"
                placeholder='{"Authorization": "Bearer ..."}'
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationTab;
